import {http} from "./api/restApiClient";
import {
  Resident,
  ResidentStatus,
  ResidentSubStatus,
} from "../pages/page-admin-residents/types";
import {CreateResidentDto} from "../pages/page-admin-residents-forms/types";
import {Value} from "@reside/forms";

export const SORT_RESIDENTS_BY = [
  "name",
  "date_of_birth",
  "created",
  "created_by",
] as const;

type SearchResidents = {
  facility_ids?: string[];
  search?: string;
  page?: number;
  size?: number;
  sort_by?: (typeof SORT_RESIDENTS_BY)[number];
  sort_direction?: "asc" | "desc";
  resident_status?: Array<ResidentStatus | ResidentSubStatus>;
  is_archived?: boolean;
  waiting_list?: boolean;
};
type LinkResidentToAdmission = {
  admission_id: string;
  resident_id: string;
};

/** Data required when looking up similar residents. */
export type SearchSimilarResidents = {
  organization_id: string;
  first_name: string | Value;
  last_name: string | Value;
  gender: string | Value;
  date_of_birth: Date | string;
};

/** The shared paginated envelope every other domain returns. */
export type ResidentPage = {
  content: Resident[];
  number: number;
  size: number;
  numberOfElements: number;
  totalElements: number;
  totalPages: number;
};

const BASE_PATH = "/residents";

export const ResidentService = {
  getResidentsViaPost: (body: SearchResidents) =>
    http.post<ResidentPage>(`${BASE_PATH}/search`, body),
  linkResidentToWorkflow: ({
    resident_id,
    admission_id,
  }: LinkResidentToAdmission) =>
    http.put<{detail: string}>(
      `${BASE_PATH}/${resident_id}/workflows/${admission_id}`,
    ),
  unlinkResidentFromWorkflow: ({
    resident_id,
    admission_id,
  }: LinkResidentToAdmission) =>
    http.delete<void>(`${BASE_PATH}/${resident_id}/workflows/${admission_id}`),
  getOneResident: (resident_uuid: string) =>
    http.get<Resident>(`${BASE_PATH}/${resident_uuid}`),
  createResident: (body: CreateResidentDto) =>
    http.post<Resident>(BASE_PATH, body),
  editResident: (body: CreateResidentDto, resident_uuid: string) =>
    http.put<Resident>(`${BASE_PATH}/${resident_uuid}`, body),
  /** Archiving and restoring are the same partial change with the other value. */
  patchResident: (
    resident_uuid: string,
    body: {is_archived?: boolean; status?: ResidentStatus},
  ) => http.patch<Resident>(`${BASE_PATH}/${resident_uuid}`, body),
  getSimilarResidentsViaPost: (body: SearchSimilarResidents) =>
    http.post<{resident: Resident; match: string}[]>(
      `${BASE_PATH}/duplicates`,
      body,
    ),
  /** Answers the resident itself, so the caller no longer needs a second request. */
  getResidentByWorkflow: (workflow_uuid: string) =>
    http.get<Resident | null>(`${BASE_PATH}/by-workflow/${workflow_uuid}`),
};
